import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NEW_API from "../../../Api/NewApi";
import Button from "../../Common/FormElements/Button/Button";
import Select from "../../Common/FormElements/Select/Select";
import { closeCenteredModal } from "../../../REDUX/actions/centeredModal";
import { CATEGORY_META, MONTH_KEYS } from "./statisticsData";

const COLUMNS = [{ key: "totalValue", label: "Total" }, ...CATEGORY_META];

/**
 * Monthly company objectives (slide 8): one row per month, a total column and
 * one column per product category. Upserts /api/objectives rows (year+month).
 */
const ObjectivesModal = ({ onSaved }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState({
    name: `${currentYear}`,
    code: currentYear,
  });
  // { [month]: { id?, totalValue, spa, ... } }
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const yearOptions = [0, 1, 2].map((offset) => ({
    name: `${currentYear + offset}`,
    code: currentYear + offset,
  }));

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const { data } = await NEW_API.get("api/objectives", {
          params: {
            "filters[year][$eq]": year.code,
            "pagination[pageSize]": 12,
          },
          signal: controller.signal,
        });
        const byMonth = {};
        (data?.data ?? []).forEach((row) => {
          byMonth[row.attributes.month] = { id: row.id, ...row.attributes };
        });
        setRows(byMonth);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [year.code]);

  const handleChange = (month, field, value) => {
    setRows((prev) => ({
      ...prev,
      [month]: { ...prev[month], [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (let month = 1; month <= 12; month++) {
        const row = rows[month];
        if (!row) continue;
        const data = { year: year.code, month };
        COLUMNS.forEach(({ key }) => {
          data[key] =
            row[key] === "" || row[key] === undefined || row[key] === null
              ? null
              : Number(row[key]);
        });
        if (row.id) {
          await NEW_API.put(`api/objectives/${row.id}`, { data });
        } else if (COLUMNS.some(({ key }) => data[key] !== null)) {
          await NEW_API.post("api/objectives", { data });
        }
      }
      toast.success(t("Objectives saved!"));
      if (onSaved) onSaved();
      dispatch(closeCenteredModal());
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 bg-white br10" style={{ minWidth: 900 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="text-blue bold f18 quickSand mb-0">{t("Objectives")}</h3>
        <div className="max-w-200px w-100">
          <Select
            name="year"
            value={year}
            options={yearOptions}
            callback={(e) => setYear(e.target.value)}
            appendToSelf
          />
        </div>
      </div>
      <div className="overflow-auto" style={{ maxHeight: 500 }}>
        <table className="table f13">
          <thead>
            <tr>
              <th>{t("Month")}</th>
              {COLUMNS.map(({ key, label }) => (
                <th key={key}>{t(`${label}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MONTH_KEYS.map((monthKey, index) => {
              const month = index + 1;
              return (
                <tr key={monthKey}>
                  <td className="sBold">{t(monthKey)}</td>
                  {COLUMNS.map(({ key }) => (
                    <td key={key}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ minWidth: 90 }}
                        disabled={loading}
                        value={rows[month]?.[key] ?? ""}
                        onChange={(e) =>
                          handleChange(month, key, e.target.value)
                        }
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="outline"
          className="me-3"
          onClick={() => dispatch(closeCenteredModal())}
        >
          {t("Cancel")}
        </Button>
        <Button loading={saving} onClick={saving ? undefined : handleSave}>
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};

export default ObjectivesModal;
